/**
 * useIsInFocusedFlow — single source of truth for "should app-root
 * overlays hide themselves right now?"
 *
 * 2026-02-24 — introduced to fix "why is a Stage-2 review dialog + a
 * running-jobs pill popping while I'm mid-lab?". Three overlays live at
 * the App root and would otherwise nag learners/instructors regardless
 * of what they're focused on:
 *   • <ActionDialogQueue />        — modal for pending action_required notifs
 *   • <GlobalJobsPill />           — floating running-jobs widget
 *   • <GenerationToastBridge />    — sonner toasts for AI-gen completions
 *
 * Each of these consults this hook. If it returns true, the overlay
 * hides itself (renders null / skips its side effect). The moment the
 * user navigates away from the focused route, the overlays resurface —
 * no data lost.
 *
 * Routes considered "focused work":
 *   • /courses/{id}/content/*          — learner Immersive Content Player
 *   • /labs/{id}/**                    — AI Lab / ML Lab runners
 *   • /industry-labs/**                — hardcoded industry lab pages
 *   • /lab-runner/**                   — Kind-2 configurable runner
 *   • /gamma-preview/**                — full-screen PPT preview
 *   • /video-preview/**                — full-screen video preview
 *   • /orgs/{orgId}/guard-digest       — instructor guard digest
 *   • /orgs/{orgId}/misconceptions     — instructor misconception board
 *   • ?focusBlock=<id>                 — Course Builder review deep-link
 *   • ?action=review                   — belt-and-braces older deep-link
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export default function useIsInFocusedFlow() {
  const location = useLocation();
  return useMemo(() => {
    if (!location) return false;
    const search = location.search || '';
    const pathname = location.pathname || '';
    if (search.includes('focusBlock=') || search.includes('action=review')) return true;
    // Any /embed/* route is a bare iframe shell (e.g. an embedded industry
    // lab). App-root overlays (toasts, jobs pill, action dialogs) must never
    // surface inside it — they'd float over the lab canvas.
    if (pathname.includes('/embed/')) return true;
    if (/\/courses\/[^/]+\/content(\/|$)/.test(pathname)) return true;
    if (/^\/labs\/[^/]+/.test(pathname)) return true;
    // The V3 experience layer (learner shell) lives at
    // /orgs/{orgId}/labs/{labId}/learn — treat it as focused work so the
    // ActionDialogQueue can't stack over the "Start investigation" button
    // (iteration 338: pre-existing Radix overlay swallowed the click on the
    // pilot Customer Churn walk).
    if (/^\/orgs\/[^/]+\/labs\/[^/]+\/learn(\/|$|\?)/.test(pathname)) return true;
    if (pathname.startsWith('/industry-labs/')) return true;
    if (pathname.startsWith('/lab-runner/')) return true;
    if (pathname.startsWith('/gamma-preview/')) return true;
    if (pathname.startsWith('/video-preview/')) return true;
    if (pathname === '/diagnostic' || pathname.startsWith('/diagnostic/')) return true;
    // Iteration 341 — defense-in-depth for the pilot-readiness cleanup.
    // /guard-digest and /misconceptions are instructor "single-task"
    // dashboards where the cohort selector / observation surface must
    // NEVER be overlaid by ActionDialogQueue. (W8.4: the /prediction-pilot
    // matcher was retired with the pilot dashboard route.)
    if (/^\/orgs\/[^/]+\/guard-digest(\/|$|\?)/.test(pathname)) return true;
    if (/^\/orgs\/[^/]+\/misconceptions(\/|$|\?)/.test(pathname)) return true;
    return false;
  }, [location]);
}
